import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CCXDataCheck,
  SUBMETRIC_FONT_SIZE,
  SUBMETRIC_ROW_WIDTHS,
} from '../helper';
import { DisplaySubMetricsProps } from '../types';
import DisplaySubMetric from './DisplaySubMetric';
import { ControlledExpander } from './Util/ControlledExpander';
import NoSubMetric from './Util/NoSubMetric';
import { BaseMetric } from '@amzn/aet-galactus-kit';

const DisplayCcxSubMetrics: React.FC<DisplaySubMetricsProps> = ({
  metrics,
  cscEntityPrefix,
}: DisplaySubMetricsProps): React.ReactElement => {
  if (!metrics.CCX?.submetrics) {
    return <></>;
  }
  if (CCXDataCheck(metrics.CCX?.submetrics!)) {
    return <NoSubMetric />;
  }
  const {
    understandability,
    attentive,
    friendliness,
    knowledgeable,
    empathy,
    courtesy,
    helpfulness,
    efficiency,
  } = metrics.CCX?.submetrics!;

  return (
    <Column spacing='none' width='100%'>
      {/* We might introduce flag that make sure Reassuring is displayed in EU 
          and Attentivee is Displayed in other regions */}
      {attentive && cscEntityPrefix !== 'cscentral_eu_user' && (
        <CCXSubMetricComponent
          metric={attentive}
          titleStringId='cs_perfportal_lighthouse_attentive'
          titleDefaultMessage='Attentive'
          definitionStringId='cs_perfportal_lighthouse_attentive_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Attentive” CCX rating.'
        />
      )}
      {friendliness && (
        <CCXSubMetricComponent
          metric={friendliness}
          titleStringId='cs_perfportal_lighthouse_friendliness'
          titleDefaultMessage='Friendliness'
          definitionStringId='cs_perfportal_lighthouse_friendliness_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Friendly” CCX rating.'
        />
      )}
      {empathy && (
        <CCXSubMetricComponent
          metric={empathy}
          titleStringId='cs_perfportal_lighthouse_empathy'
          titleDefaultMessage='Empathy'
          definitionStringId='cs_perfportal_lighthouse_empathy_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Empathy” Customer Contact Experience (CCX) rating.'
        />
      )}
      {courtesy && (
        <CCXSubMetricComponent
          metric={courtesy}
          titleStringId='cs_perfportal_lighthouse_courtesy'
          titleDefaultMessage='Courtesy'
          definitionStringId='cs_perfportal_lighthouse_courtesy_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Courtesy” Customer Contact Experience (CCX) rating.'
        />
      )}
      {knowledgeable && (
        <CCXSubMetricComponent
          metric={knowledgeable}
          titleStringId='cs_perfportal_lighthouse_knowledgeable'
          titleDefaultMessage='Knowledgeable'
          definitionStringId='cs_perfportal_lighthouse_knowledgeable_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Knowledgeable” CCX rating.'
        />
      )}
      {/* We might introduce flag that make sure Reassuring is displayed in EU 
          and Attentivee is Displayed in other regions */}
      {attentive && cscEntityPrefix === 'cscentral_eu_user' && (
        <CCXSubMetricComponent
          metric={attentive}
          titleStringId='cs_perfportal_lighthouse_reassuring'
          titleDefaultMessage='Reassuring'
          definitionStringId='cs_perfportal_lighthouse_reassuring_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Reassuring” CCX rating.'
        />
      )}
      {understandability && (
        <CCXSubMetricComponent
          metric={understandability}
          titleStringId='cs_perfportal_lighthouse_understandability'
          titleDefaultMessage='Understandability'
          definitionStringId='cs_perfportal_lighthouse_understandability_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Easy to Understand” CCX rating.'
        />
      )}
      {helpfulness && (
        <CCXSubMetricComponent
          metric={helpfulness}
          titleStringId='cs_perfportal_lighthouse_helpfulness'
          titleDefaultMessage='Helpfulness'
          definitionStringId='cs_perfportal_lighthouse_helpfulness_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Helpfulness” Customer Contact Experience (CCX) rating.'
        />
      )}
      {efficiency && (
        <CCXSubMetricComponent
          metric={efficiency}
          titleStringId='cs_perfportal_lighthouse_efficiency'
          titleDefaultMessage='Efficiency'
          definitionStringId='cs_perfportal_lighthouse_efficiency_def'
          definitionDefaultMessage='Percentage of 4 to 5-star responses to the “Efficiency” Customer Contact Experience (CCX) rating.'
        />
      )}
    </Column>
  );
};

interface CCXSubMetricComponentProps {
  metric: BaseMetric;
  titleStringId: string;
  titleDefaultMessage: string;
  definitionStringId: string;
  definitionDefaultMessage: string;
}

const CCXSubMetricComponent: React.FC<CCXSubMetricComponentProps> = ({
  metric,
  titleStringId,
  titleDefaultMessage,
  definitionStringId,
  definitionDefaultMessage,
}: CCXSubMetricComponentProps): React.ReactElement => {
  return (
    <Row
      alignmentVertical='top'
      width='100%'
      widths={SUBMETRIC_ROW_WIDTHS}
      spacing='none'
      spacingInset='none none 200'
    >
      <ControlledExpander
        title={
          <Text
            type={SUBMETRIC_FONT_SIZE}
            className='lighthouse-submetric-long-word-treatment'
          >
            <FormattedMessage
              id={titleStringId}
              defaultMessage={titleDefaultMessage}
            />
          </Text>
        }
        type='list'
      >
        <Text type={SUBMETRIC_FONT_SIZE}>
          <FormattedMessage
            id={definitionStringId}
            defaultMessage={definitionDefaultMessage}
          />
        </Text>
      </ControlledExpander>
      <DisplaySubMetric value={metric.datapoint} isPercentage isBold />
      <DisplaySubMetric
        value={metric.networkAverage!}
        isPercentage
        datapointNull={metric.datapoint === null}
      />
    </Row>
  );
};

export default DisplayCcxSubMetrics;
